import * as React from 'react';
import { Grid2 as Grid, Card, CardContent, Tabs, Tab, TabProps, TabsProps } from '@mui/material';
import { ActionRequest } from '../../actions';
import { Loading, LineChart } from '../common';
import { Moment } from 'moment';
import { styled } from '@mui/styles';
import { AxisValueFormatterContext } from '@mui/x-charts/models/axis';
import moment from 'moment/moment';
import { ReportFilterDates } from './ReportFilterDates';

interface Props {
    readonly styled: StyledProps;
}

type ReportType = 'Retention' | '7DaysRetention' | '28DaysRetention';
interface State {
    readonly loading: boolean;
    readonly startTimeSpam: Moment;
    readonly endTimeSpam: Moment;
    readonly reportType: ReportType;
    readonly retentionData?: AnalyticsModels.RetentionReport[];
    readonly retentionDayData?: AnalyticsModels.DayValueReport[];
}

export class RetentionReport extends React.PureComponent<Props, State> {
    state: State = {
        loading: true,
        reportType: 'Retention',
        startTimeSpam: moment.utc().subtract(7, 'days'),
        endTimeSpam: moment.utc(),
    };

    componentDidMount() {
        this.getRetentionData().then();
    }

    render() {
        const { styled } = this.props;
        const { loading, startTimeSpam, endTimeSpam, reportType, retentionData, retentionDayData } = this.state;

        const selected = reportType === 'Retention' ? 0 : reportType === '7DaysRetention' ? 1 : 2;

        const changeSubTab = (event: any, value: any) => {
            switch (value) {
                case 0:
                    this.setState({ reportType: 'Retention' });
                    this.getRetentionData().then();
                    break;
                case 1:
                    this.setState({ reportType: '7DaysRetention' });
                    this.get7DayRetentionData().then();
                    break;
                case 2:
                    this.setState({ reportType: '28DaysRetention' });
                    this.get28DayRetentionData().then();
                    break;
            }
        };

        const onFilterDates = (start: Moment, end: Moment) => {
            this.setState({ startTimeSpam: start, endTimeSpam: end});
            this.getRetentionData(start, end).then();
        };

        return (
            <Card style={{ padding: 0 }}>
                <CardContent style={{ padding: '0px 10px' }}>
                    <Grid container={true} justifyContent="center" spacing={0}>
                        <Grid size={12}>
                            <StyledTabs
                                styled={styled}
                                value={selected}
                                onChange={changeSubTab}
                                indicatorColor="primary"
                                textColor="primary"
                                variant="scrollable"
                                allowScrollButtonsMobile={true}
                                scrollButtons="auto"
                            >
                                <StyledTab
                                    styled={styled}
                                    label={'Retention'}
                                    value={0}
                                />
                                <StyledTab
                                    styled={styled}
                                    label={'7 Days Retention'}
                                    value={1}
                                />
                                <StyledTab
                                    styled={styled}
                                    label={'28 Days Retention'}
                                    value={2}
                                />
                            </StyledTabs>
                        </Grid>
                        {loading && (!retentionData || !retentionDayData) && (
                            <Grid size={12}>
                                <div style={{ height: 300 }}>
                                    <Loading styled={styled} />
                                </div>
                            </Grid>
                        )}
                        {!loading && retentionData && (
                            <>
                                <Grid size={12}>
                                    <LineChart
                                        styled={styled}
                                        series={[
                                            {
                                                curve: 'linear',
                                                data: retentionData.map(d => d.value),
                                                color: '#1973e8',
                                            },
                                        ]}
                                        xAxis={[
                                            {
                                                scaleType: 'point',
                                                data: retentionData.map(d => d.day)
                                            }
                                        ]}
                                        yAxis={[
                                            {
                                                valueFormatter: (value: number, context: AxisValueFormatterContext) => `${value}%`
                                            }
                                        ]}
                                        height={300}
                                        grid={{ horizontal: true }}
                                        sx={{
                                            '& .MuiChartsAxis-tickLabel': {
                                                fill: styled.themeMode === 'light' ? '#7d8085' : '#a7aaae !important',
                                            },
                                            '& .MuiChartsAxis-tick': {
                                                display: 'none',
                                            },
                                            '& .MuiChartsAxis-line': {
                                                display: 'none',
                                            },
                                            '& .MuiChartsGrid-line': {
                                                stroke: styled.themeMode === 'light' ? '#eeeeee' : '#303030 !important',
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <ReportFilterDates
                                        styled={this.props.styled}
                                        start={startTimeSpam}
                                        end={endTimeSpam}
                                        onFilter={onFilterDates}
                                    />
                                </Grid>
                            </>
                        )}
                        {!loading && retentionDayData && (
                            <>
                                <Grid size={12}>
                                    <LineChart
                                        styled={styled}
                                        series={[
                                            {
                                                curve: 'linear',
                                                data: retentionDayData.map(d => d.value),
                                                color: '#1973e8',
                                            },
                                        ]}
                                        xAxis={[
                                            {
                                                scaleType: 'point',
                                                data: retentionDayData.map(d => d.day),
                                                valueFormatter: (value: string, context: AxisValueFormatterContext) => moment(value).format('MMM D')
                                            }
                                        ]}
                                        height={300}
                                        grid={{ horizontal: true }}
                                        sx={{
                                            '& .MuiChartsAxis-tickLabel': {
                                                fill: styled.themeMode === 'light' ? '#7d8085' : '#a7aaae !important',
                                            },
                                            '& .MuiChartsAxis-tick': {
                                                display: 'none',
                                            },
                                            '& .MuiChartsAxis-line': {
                                                display: 'none',
                                            },
                                            '& .MuiChartsGrid-line': {
                                                stroke: styled.themeMode === 'light' ? '#eeeeee' : '#303030 !important',
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <ReportFilterDates
                                        styled={this.props.styled}
                                        start={startTimeSpam}
                                        end={endTimeSpam}
                                        onFilter={onFilterDates}
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                </CardContent>
            </Card>
        );
    }

    private async getRetentionData(start?: Moment, end?: Moment): Promise<void> {
        this.setState({
            loading: true,
            retentionData: undefined,
            retentionDayData: undefined,
        });
        const startTimeSpam = start || this.state.startTimeSpam;
        const endTimeSpam = end || this.state.endTimeSpam;
        const url = `analytics/retentionReport`;
        const data = await ActionRequest.get<AnalyticsModels.RetentionReport[]>(
            url,
            {
                startDate: startTimeSpam.format('YYYY-MM-DD'),
                endDate: endTimeSpam.format('YYYY-MM-DD'),
            }
        );
        this.setState({
            loading: false,
            retentionData: data || undefined,
        });
    }

    private async get7DayRetentionData(start?: Moment, end?: Moment): Promise<void> {
        this.setState({
            loading: true,
            retentionData: undefined,
            retentionDayData: undefined,
        });
        const startTimeSpam = start || this.state.startTimeSpam;
        const endTimeSpam = end || this.state.endTimeSpam;
        const url = `analytics/active7DayRetentionUsersReport`;
        const data = await ActionRequest.get<AnalyticsModels.DayValueReport[]>(
            url,
            {
                startDate: startTimeSpam.format('YYYY-MM-DD'),
                endDate: endTimeSpam.format('YYYY-MM-DD'),
            }
        );
        this.setState({
            loading: false,
            retentionDayData: data || undefined,
        });
    }

    private async get28DayRetentionData(start?: Moment, end?: Moment): Promise<void> {
        this.setState({
            loading: true,
            retentionData: undefined,
            retentionDayData: undefined,
        });
        const startTimeSpam = start || this.state.startTimeSpam;
        const endTimeSpam = end || this.state.endTimeSpam;
        const url = `analytics/active28DayRetentionUsersReport`;
        const data = await ActionRequest.get<AnalyticsModels.DayValueReport[]>(
            url,
            {
                startDate: startTimeSpam.format('YYYY-MM-DD'),
                endDate: endTimeSpam.format('YYYY-MM-DD'),
            }
        );
        this.setState({
            loading: false,
            retentionDayData: data || undefined,
        });
    }
}

interface StyledTabsProps extends TabsProps {
    readonly styled: StyledProps;
}
const StyledTabs = styled((props: StyledTabsProps) => (
    <Tabs
        {...props}
        sx={{
            padding: 0,
            height: 60,
            borderTop: 'none',
            borderBottom: 'none',
        }}
    >
        {props.children}
    </Tabs>
))(() => ({}));

interface StyledTabProps extends TabProps {
    readonly styled: StyledProps;
}
const StyledTab = styled((props: StyledTabProps) => (
    <Tab
        {...props}
        sx={{
            height: 58,
            fontSize: 14,
            padding: '4px 10px',
            margin: '0px 10px',
            '&.MuiTab-root': {
                color: props.styled.themeMode === 'light' ? '#5f6368' : '#8a8e93',
                '&:hover': {
                    backgroundColor: props.styled.themeMode === 'light' ? '#f1f2f3' : '#292b2c',
                }
            },
            '&.Mui-selected': {
                color: props.styled.themeMode === 'light' ? '#1a73e8' : '#6ba5ff',
                borderTop: props.styled.themeMode === 'light' ? '4px solid #1a73e8' : '4px solid #6ba5ff',
                borderBottom: 'none',
                '&:hover': {
                    backgroundColor: props.styled.themeMode === 'light' ? '#f1f4f9' : '#25282f',
                }
            }
        }}
    >
        {props.children}
    </Tab>
))(() => ({}));